import React, { useEffect, useState, useCallback } from 'react';
import { Pencil, Plus, X, Save, GripVertical, ArrowUp, ArrowDown, Check, AlertTriangle } from 'lucide-react';
import { useApi } from './DashboardLayout';

export default function DropdownManagementPage() {
  const [dropdowns, setDropdowns] = useState([]);
  const [selectedDd, setSelectedDd] = useState(null);
  const [ddValues, setDdValues] = useState([]);
  const [newVal, setNewVal] = useState('');
  const [editingIdx, setEditingIdx] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [saving, setSaving] = useState(false);
  const [feedback, setFeedback] = useState(null);
  const { apiFetch } = useApi();

  useEffect(() => { loadDropdowns(); }, []);

  const loadDropdowns = async () => {
    const r = await apiFetch('/api/dropdowns');
    const d = await r?.json();
    if (d) setDropdowns(d);
  };

  const selectDropdown = (dd) => {
    setSelectedDd(dd);
    setDdValues(Array.isArray(dd.dropdown_values) ? [...dd.dropdown_values] : []);
    setNewVal('');
    setEditingIdx(null);
    setFeedback(null);
  };

  const addValue = () => {
    const val = newVal.trim();
    if (val && !ddValues.includes(val)) {
      setDdValues(prev => [...prev, val]);
      setNewVal('');
    }
  };

  const removeValue = (idx) => {
    setDdValues(prev => prev.filter((_, i) => i !== idx));
  };

  const moveItem = (idx, direction) => {
    const newIdx = idx + direction;
    if (newIdx < 0 || newIdx >= ddValues.length) return;
    const updated = [...ddValues];
    [updated[idx], updated[newIdx]] = [updated[newIdx], updated[idx]];
    setDdValues(updated);
  };

  const startRename = (idx) => {
    setEditingIdx(idx);
    setEditValue(ddValues[idx]);
  };

  const cancelRename = () => {
    setEditingIdx(null);
    setEditValue('');
  };

  const confirmRename = async (idx) => {
    const oldValue = ddValues[idx];
    const newValue = editValue.trim();

    if (!newValue || newValue === oldValue) {
      cancelRename();
      return;
    }

    if (ddValues.includes(newValue)) {
      setFeedback({ type: 'error', text: `"${newValue}" existiert bereits.` });
      return;
    }

    setSaving(true);
    try {
      const r = await apiFetch(`/api/dropdowns/${selectedDd.id}/rename-item`, {
        method: 'PUT',
        body: JSON.stringify({ old_value: oldValue, new_value: newValue })
      });
      const d = await r?.json();
      if (d?.status) {
        const updated = [...ddValues];
        updated[idx] = newValue;
        setDdValues(updated);
        setFeedback({
          type: 'success',
          text: d.updated_offers > 0
            ? `Umbenannt. ${d.updated_offers} Angebot(e) aktualisiert.`
            : 'Wert umbenannt.'
        });
        loadDropdowns();
      } else {
        setFeedback({ type: 'error', text: d?.detail || 'Fehler beim Umbenennen.' });
      }
    } catch {
      setFeedback({ type: 'error', text: 'Netzwerkfehler.' });
    }
    setSaving(false);
    setEditingIdx(null);
    setEditValue('');
  };

  const saveOrder = async () => {
    setSaving(true);
    try {
      const r = await apiFetch(`/api/dropdowns/${selectedDd.id}/reorder`, {
        method: 'PUT',
        body: JSON.stringify({ values: ddValues })
      });
      const d = await r?.json();
      if (d?.status) {
        setFeedback({ type: 'success', text: 'Reihenfolge gespeichert.' });
        loadDropdowns();
      }
    } catch {
      setFeedback({ type: 'error', text: 'Fehler beim Speichern.' });
    }
    setSaving(false);
  };

  const saveValues = async () => {
    setSaving(true);
    try {
      const r = await apiFetch(`/api/dropdowns/${selectedDd.id}`, {
        method: 'PUT',
        body: JSON.stringify({ values: ddValues })
      });
      const d = await r?.json();
      if (d?.status) {
        setFeedback({ type: 'success', text: 'Dropdown gespeichert.' });
        loadDropdowns();
      }
    } catch {
      setFeedback({ type: 'error', text: 'Fehler beim Speichern.' });
    }
    setSaving(false);
  };

  const hasOrderChanged = useCallback(() => {
    if (!selectedDd) return false;
    const original = Array.isArray(selectedDd.dropdown_values) ? selectedDd.dropdown_values : [];
    if (ddValues.length !== original.length) return true;
    return ddValues.some((v, i) => v !== original[i]);
  }, [selectedDd, ddValues]);

  return (
    <div data-testid="dropdown-management-page">
      <div className="page-header">
        <h2>Dropdown-Verwaltung</h2>
        <p>Dropdown-Listen verwalten, umbenennen und umsortieren</p>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '280px 1fr', gap: '1rem', alignItems: 'start' }}>
        {/* Left: Dropdown list */}
        <div className="card">
          <div className="card-body" style={{ padding: 0 }}>
            <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--border)', fontWeight: 700, fontSize: 14 }}>
              Dropdown-Listen
            </div>
            {dropdowns.map(dd => (
              <div
                key={dd.id}
                data-testid={`dropdown-select-${dd.id}`}
                onClick={() => selectDropdown(dd)}
                style={{
                  padding: '10px 16px',
                  cursor: 'pointer',
                  borderBottom: '1px solid var(--border)',
                  background: selectedDd?.id === dd.id ? 'var(--primary-light, rgba(204,0,0,0.08))' : 'transparent',
                  borderLeft: selectedDd?.id === dd.id ? '3px solid var(--primary)' : '3px solid transparent',
                  transition: 'all 0.15s'
                }}
              >
                <div style={{ fontWeight: 600, fontSize: 13 }}>{dd.name}</div>
                <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 2 }}>
                  {(Array.isArray(dd.dropdown_values) ? dd.dropdown_values : []).length} Werte
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Right: Edit panel */}
        <div className="card">
          <div className="card-body">
            {!selectedDd ? (
              <div style={{ textAlign: 'center', padding: '3rem 1rem', color: 'var(--text-secondary)' }}>
                <GripVertical size={32} style={{ marginBottom: 8, opacity: 0.4 }} />
                <p style={{ fontSize: 14 }}>Wählen Sie eine Dropdown-Liste aus, um sie zu bearbeiten.</p>
              </div>
            ) : (
              <>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                  <h4 style={{ fontSize: 16, fontWeight: 700 }}>{selectedDd.name}</h4>
                  <div style={{ display: 'flex', gap: 8 }}>
                    {hasOrderChanged() && (
                      <button
                        className="btn btn-primary btn-sm"
                        onClick={saveValues}
                        disabled={saving}
                        data-testid="dropdown-save-btn"
                      >
                        <Save size={14} /> Speichern
                      </button>
                    )}
                  </div>
                </div>

                {feedback && (
                  <div
                    data-testid="dropdown-feedback"
                    style={{
                      padding: '8px 12px',
                      marginBottom: '1rem',
                      borderRadius: 6,
                      fontSize: 13,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                      background: feedback.type === 'success' ? 'rgba(16,185,129,0.1)' : 'rgba(239,68,68,0.1)',
                      color: feedback.type === 'success' ? '#10B981' : '#EF4444',
                      border: `1px solid ${feedback.type === 'success' ? 'rgba(16,185,129,0.2)' : 'rgba(239,68,68,0.2)'}`
                    }}
                  >
                    {feedback.type === 'success' ? <Check size={14} /> : <AlertTriangle size={14} />}
                    {feedback.text}
                  </div>
                )}

                {/* Add new value */}
                <div style={{ display: 'flex', gap: 8, marginBottom: '1rem' }}>
                  <input
                    className="form-input"
                    placeholder="Neuen Wert hinzufügen..."
                    value={newVal}
                    onChange={e => setNewVal(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), addValue())}
                    data-testid="dropdown-new-value-input"
                    style={{ flex: 1 }}
                  />
                  <button className="btn btn-primary btn-sm" onClick={addValue} data-testid="dropdown-add-value-btn">
                    <Plus size={14} /> Hinzufügen
                  </button>
                </div>

                {/* Values list with reorder + rename */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                  {ddValues.map((val, idx) => (
                    <div
                      key={idx}
                      data-testid={`dropdown-item-${idx}`}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 8,
                        padding: '8px 12px',
                        background: 'var(--bg-page, #F3F4F6)',
                        borderRadius: 6,
                        border: '1px solid var(--border)',
                        fontSize: 13
                      }}
                    >
                      {/* Reorder buttons */}
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <button
                          className="btn-icon"
                          onClick={() => moveItem(idx, -1)}
                          disabled={idx === 0}
                          data-testid={`dropdown-move-up-${idx}`}
                          style={{ padding: 2, opacity: idx === 0 ? 0.3 : 1 }}
                          title="Nach oben"
                        >
                          <ArrowUp size={12} />
                        </button>
                        <button
                          className="btn-icon"
                          onClick={() => moveItem(idx, 1)}
                          disabled={idx === ddValues.length - 1}
                          data-testid={`dropdown-move-down-${idx}`}
                          style={{ padding: 2, opacity: idx === ddValues.length - 1 ? 0.3 : 1 }}
                          title="Nach unten"
                        >
                          <ArrowDown size={12} />
                        </button>
                      </div>

                      {/* Value or edit field */}
                      <div style={{ flex: 1 }}>
                        {editingIdx === idx ? (
                          <input
                            className="form-input"
                            value={editValue}
                            onChange={e => setEditValue(e.target.value)}
                            onKeyDown={e => {
                              if (e.key === 'Enter') { e.preventDefault(); confirmRename(idx); }
                              if (e.key === 'Escape') cancelRename();
                            }}
                            autoFocus
                            data-testid={`dropdown-rename-input-${idx}`}
                            style={{ padding: '4px 8px', fontSize: 13 }}
                          />
                        ) : (
                          <span>{val}</span>
                        )}
                      </div>

                      {/* Action buttons */}
                      <div style={{ display: 'flex', gap: 4 }}>
                        {editingIdx === idx ? (
                          <>
                            <button
                              className="btn-icon"
                              onClick={() => confirmRename(idx)}
                              disabled={saving}
                              data-testid={`dropdown-rename-confirm-${idx}`}
                              title="Bestätigen"
                              style={{ color: '#10B981' }}
                            >
                              <Check size={14} />
                            </button>
                            <button
                              className="btn-icon"
                              onClick={cancelRename}
                              data-testid={`dropdown-rename-cancel-${idx}`}
                              title="Abbrechen"
                            >
                              <X size={14} />
                            </button>
                          </>
                        ) : (
                          <>
                            <button
                              className="btn-icon edit"
                              onClick={() => startRename(idx)}
                              data-testid={`dropdown-rename-${idx}`}
                              title="Umbenennen"
                            >
                              <Pencil size={13} />
                            </button>
                            <button
                              className="btn-icon"
                              onClick={() => removeValue(idx)}
                              data-testid={`dropdown-remove-${idx}`}
                              title="Entfernen"
                              style={{ color: '#EF4444' }}
                            >
                              <X size={14} />
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                  ))}
                </div>

                {ddValues.length === 0 && (
                  <p style={{ textAlign: 'center', color: 'var(--text-secondary)', padding: '1rem', fontSize: 13 }}>
                    Keine Werte vorhanden.
                  </p>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
